import React, { useState, useEffect, useMemo } from "react";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
    ChevronLeft, ChevronRight, Plus, X, Video, MapPin, Bell, StickyNote,
    CheckCircle2, XCircle, Clock, Trash2, ClipboardList
} from "lucide-react";

const MONTHS_RO = [
    "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
    "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"
];
const DAYS_RO = ["Lun", "Mar", "Mie", "Joi", "Vin", "Sâm", "Dum"];

const EVENT_TYPE_CONFIG = {
    note:              { label: "Notiță",        color: "#6B7280", bg: "#F3F4F6", Icon: StickyNote },
    reminder:          { label: "Reminder",       color: "#F59E0B", bg: "#FEF3C7", Icon: Bell },
    meeting_online:    { label: "Meeting online", color: "#3B82F6", bg: "#DBEAFE", Icon: Video },
    meeting_physical:  { label: "Meeting fizic",  color: "#10B981", bg: "#D1FAE5", Icon: MapPin },
    task_deadline:     { label: "Task deadline",  color: "#002FA7", bg: "#E0E7FF", Icon: ClipboardList },
};

function formatDateISO(d) {
    // YYYY-MM-DD without TZ shift
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function sameDay(iso, d) {
    if (!iso) return false;
    const ev = new Date(iso);
    return ev.getFullYear() === d.getFullYear() && ev.getMonth() === d.getMonth() && ev.getDate() === d.getDate();
}

export default function CalendarPage() {
    const navigate = useNavigate();
    const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [users, setUsers] = useState([]);

    const [form, setForm] = useState({
        title: "",
        description: "",
        start_date: "",
        end_date: "",
        all_day: false,
        event_type: "meeting_online",
        location: "",
        participants: [],
    });
    const [saving, setSaving] = useState(false);

    const monthStart = useMemo(() => { const d = new Date(cursor); d.setDate(1); return d; }, [cursor]);
    const monthEnd = useMemo(() => { const d = new Date(cursor); d.setMonth(d.getMonth() + 1); d.setDate(0); return d; }, [cursor]);

    const fetchEvents = () => {
        setLoading(true);
        const start = formatDateISO(monthStart) + "T00:00:00";
        const end = formatDateISO(monthEnd) + "T23:59:59";
        API.get(`/calendar/events?start=${start}&end=${end}`)
            .then(r => setEvents(r.data))
            .catch(() => toast.error("Eroare la încărcarea calendarului"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchEvents(); /* eslint-disable-next-line */ }, [cursor]);
    useEffect(() => { API.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);

    // Build calendar grid (6 weeks)
    const grid = useMemo(() => {
        const firstDay = new Date(monthStart);
        // Shift so Monday = first day of week
        let offset = firstDay.getDay() - 1; // 0=Sun => -1, 1=Mon => 0
        if (offset < 0) offset = 6;
        const gridStart = new Date(firstDay);
        gridStart.setDate(gridStart.getDate() - offset);
        const cells = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(gridStart);
            d.setDate(gridStart.getDate() + i);
            cells.push(d);
        }
        return cells;
    }, [monthStart]);

    const eventsByDay = useMemo(() => {
        const map = {};
        events.forEach(e => {
            if (!e.start_date) return;
            const k = formatDateISO(new Date(e.start_date));
            if (!map[k]) map[k] = [];
            map[k].push(e);
        });
        return map;
    }, [events]);

    const openCreate = (date) => {
        const d = new Date(date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const startStr = `${y}-${m}-${day}T09:00`;
        const endStr   = `${y}-${m}-${day}T10:00`;
        setForm({
            title: "", description: "", start_date: startStr, end_date: endStr,
            all_day: false, event_type: "meeting_online", location: "", participants: []
        });
        setShowCreate(true);
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) { toast.error("Titlu obligatoriu"); return; }
        if (!form.start_date) { toast.error("Data de început obligatorie"); return; }
        setSaving(true);
        try {
            await API.post("/calendar/events", {
                title: form.title.trim(),
                description: form.description,
                start_date: new Date(form.start_date).toISOString(),
                end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
                all_day: form.all_day,
                event_type: form.event_type,
                location: form.location,
                participants: form.participants
            });
            toast.success("Eveniment creat");
            setShowCreate(false);
            fetchEvents();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const respondEvent = async (eventId, action) => {
        try {
            await API.post(`/calendar/events/${eventId}/respond`, { action });
            toast.success(action === "accept" ? "Invitație acceptată" : "Invitație refuzată");
            fetchEvents();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const deleteEvent = async (eventId) => {
        if (!window.confirm("Sigur ștergi acest eveniment?")) return;
        try {
            await API.delete(`/calendar/events/${eventId}`);
            toast.success("Eveniment șters");
            fetchEvents();
            setSelectedDate(null);
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const today = new Date();
    const isToday = (d) => sameDay(d.toISOString(), today);
    const inMonth = (d) => d.getMonth() === monthStart.getMonth();

    const dayEvents = selectedDate ? (eventsByDay[formatDateISO(selectedDate)] || []) : [];

    return (
        <div className="p-6 max-w-[1400px] mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Calendar</h1>
                    <p className="text-sm text-[#9CA3AF] dark:text-[#7E858E] mt-1">Deadlines, meetinguri, remindere și notițe</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => { const d = new Date(cursor); d.setMonth(d.getMonth() - 1); setCursor(d); }}
                        className="p-2 rounded-md border border-[#E4E4E7] dark:border-[#252B33] bg-white dark:bg-[#151A21] text-[#4B5563] dark:text-[#B5BAC1] hover:bg-[#F1F3F5] dark:hover:bg-[#1C222A]"
                        data-testid="calendar-prev-month">
                        <ChevronLeft size={18} />
                    </button>
                    <div className="px-4 py-2 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md font-semibold text-[#111827] dark:text-[#F3F4F6] min-w-[180px] text-center">
                        {MONTHS_RO[cursor.getMonth()]} {cursor.getFullYear()}
                    </div>
                    <button onClick={() => { const d = new Date(cursor); d.setMonth(d.getMonth() + 1); setCursor(d); }}
                        className="p-2 rounded-md border border-[#E4E4E7] dark:border-[#252B33] bg-white dark:bg-[#151A21] text-[#4B5563] dark:text-[#B5BAC1] hover:bg-[#F1F3F5] dark:hover:bg-[#1C222A]"
                        data-testid="calendar-next-month">
                        <ChevronRight size={18} />
                    </button>
                    <button onClick={() => setCursor(() => { const d = new Date(); d.setDate(1); return d; })}
                        className="px-3 py-2 text-sm rounded-md border border-[#E4E4E7] dark:border-[#252B33] bg-white dark:bg-[#151A21] text-[#4B5563] dark:text-[#B5BAC1] hover:bg-[#F1F3F5] dark:hover:bg-[#1C222A]"
                        data-testid="calendar-today">Azi</button>
                    <button onClick={() => openCreate(new Date())}
                        className="ml-2 flex items-center gap-1.5 bg-[#002FA7] text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-[#002482]"
                        data-testid="calendar-create-event">
                        <Plus size={15} /> Eveniment nou
                    </button>
                </div>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
                {DAYS_RO.map(d => (
                    <div key={d} className="text-xs font-bold text-[#9CA3AF] dark:text-[#7E858E] uppercase tracking-wider text-center py-2">{d}</div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
                {grid.map((d, idx) => {
                    const key = formatDateISO(d);
                    const dayEvs = eventsByDay[key] || [];
                    return (
                        <button key={idx}
                            onClick={() => setSelectedDate(d)}
                            data-testid={`calendar-day-${key}`}
                            className={`text-left min-h-[100px] p-2 rounded-md border transition-colors overflow-hidden
                                ${inMonth(d) ? "bg-white dark:bg-[#151A21]" : "bg-[#F8F9FA] dark:bg-[#0B0D10]"}
                                ${isToday(d) ? "border-[#002FA7] ring-1 ring-[#002FA7]" : "border-[#E4E4E7] dark:border-[#252B33]"}
                                hover:border-[#002FA7]/40
                            `}>
                            <div className={`text-xs font-bold mb-1 ${inMonth(d) ? "text-[#111827] dark:text-[#F3F4F6]" : "text-[#C7C7C7]"} ${isToday(d) ? "text-[#002FA7] dark:text-[#6A91FF]" : ""}`}>
                                {d.getDate()}
                            </div>
                            <div className="space-y-0.5">
                                {dayEvs.slice(0, 3).map((e, i) => {
                                    const cfg = EVENT_TYPE_CONFIG[e.event_type] || EVENT_TYPE_CONFIG.note;
                                    return (
                                        <div key={i} className="flex items-center gap-1 text-[10px] truncate rounded px-1 py-0.5"
                                            style={{ backgroundColor: cfg.bg, color: cfg.color }}
                                            title={e.title}>
                                            <cfg.Icon size={9} />
                                            <span className="truncate font-semibold">{e.title}</span>
                                        </div>
                                    );
                                })}
                                {dayEvs.length > 3 && (
                                    <div className="text-[10px] text-[#9CA3AF] font-semibold">+{dayEvs.length - 3}</div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {loading && <p className="text-center text-sm text-[#9CA3AF] mt-4">Se încarcă...</p>}

            {/* Day details drawer */}
            {selectedDate && (
                <div className="fixed inset-0 z-40 flex">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedDate(null)} />
                    <div className="relative ml-auto bg-white dark:bg-[#151A21] w-full max-w-md h-full overflow-y-auto shadow-2xl border-l border-[#E4E4E7] dark:border-[#252B33]">
                        <div className="px-5 py-4 border-b border-[#E4E4E7] dark:border-[#252B33] flex items-center justify-between sticky top-0 bg-white dark:bg-[#151A21] z-10">
                            <div>
                                <h2 className="font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                    {selectedDate.toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                                </h2>
                                <p className="text-xs text-[#9CA3AF] mt-0.5">{dayEvents.length} evenimente</p>
                            </div>
                            <button onClick={() => setSelectedDate(null)} className="text-[#9CA3AF] hover:text-[#111827] dark:hover:text-white"><X size={20} /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <button onClick={() => openCreate(selectedDate)}
                                className="w-full flex items-center justify-center gap-1.5 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482]">
                                <Plus size={15} /> Eveniment nou în această zi
                            </button>
                            {dayEvents.length === 0 && (
                                <p className="text-center text-sm text-[#9CA3AF] py-8">Nu sunt evenimente în această zi</p>
                            )}
                            {dayEvents.map(e => {
                                const cfg = EVENT_TYPE_CONFIG[e.event_type] || EVENT_TYPE_CONFIG.note;
                                const isTask = e.source === "task";
                                return (
                                    <div key={e.id} className="border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-3 bg-white dark:bg-[#1C222A]"
                                        data-testid={`event-item-${e.id}`}>
                                        <div className="flex items-start gap-2">
                                            <div className="rounded-md p-2 flex-shrink-0" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                                                <cfg.Icon size={16} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.label}</span>
                                                    {e.my_status === "pending" && !isTask && (
                                                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">Nou</span>
                                                    )}
                                                    {e.my_status === "accepted" && !isTask && (
                                                        <CheckCircle2 size={14} className="text-green-500" />
                                                    )}
                                                </div>
                                                <p className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6] mt-0.5">{e.title}</p>
                                                {e.description && <p className="text-xs text-[#4B5563] dark:text-[#B5BAC1] mt-1 line-clamp-3">{e.description}</p>}
                                                {!e.all_day && e.start_date && (
                                                    <p className="text-xs text-[#9CA3AF] mt-1 flex items-center gap-1">
                                                        <Clock size={11} />
                                                        {new Date(e.start_date).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                                                        {e.end_date && e.end_date !== e.start_date && (
                                                            <> – {new Date(e.end_date).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}</>
                                                        )}
                                                    </p>
                                                )}
                                                {e.location && (
                                                    <p className="text-xs text-[#9CA3AF] mt-0.5 flex items-center gap-1"><MapPin size={11} /> {e.location}</p>
                                                )}
                                                {isTask && (
                                                    <button onClick={() => navigate(`/boards/${e.board_id}?task=${e.task_id}`)}
                                                        className="text-xs text-[#002FA7] dark:text-[#6A91FF] font-semibold hover:underline mt-1">
                                                        Deschide pe board: {e.board_name}
                                                    </button>
                                                )}
                                                {!isTask && e.my_status === "pending" && (
                                                    <div className="flex gap-2 mt-2">
                                                        <button onClick={() => respondEvent(e.id, "accept")}
                                                            data-testid={`event-accept-${e.id}`}
                                                            className="flex-1 text-xs bg-green-600 text-white py-1.5 rounded hover:bg-green-700 font-semibold">
                                                            Accept
                                                        </button>
                                                        <button onClick={() => respondEvent(e.id, "decline")}
                                                            data-testid={`event-decline-${e.id}`}
                                                            className="flex-1 text-xs bg-red-100 text-red-700 py-1.5 rounded hover:bg-red-200 font-semibold">
                                                            Refuz
                                                        </button>
                                                    </div>
                                                )}
                                                {!isTask && (
                                                    <button onClick={() => deleteEvent(e.id)}
                                                        className="mt-2 flex items-center gap-1 text-[11px] text-red-500 hover:text-red-700">
                                                        <Trash2 size={11} /> Șterge
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Create event modal */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)} />
                    <div className="relative bg-white dark:bg-[#151A21] rounded-lg shadow-2xl w-full max-w-lg z-10 border border-[#E4E4E7] dark:border-[#252B33]">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E4E7] dark:border-[#252B33]">
                            <h2 className="font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Eveniment nou</h2>
                            <button onClick={() => setShowCreate(false)} className="text-[#9CA3AF] hover:text-[#111827] dark:hover:text-white"><X size={18} /></button>
                        </div>
                        <form onSubmit={submitCreate} className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Titlu *</label>
                                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                                    data-testid="event-form-title"
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Tip</label>
                                <div className="grid grid-cols-4 gap-1.5">
                                    {["note", "reminder", "meeting_online", "meeting_physical"].map(t => {
                                        const cfg = EVENT_TYPE_CONFIG[t];
                                        return (
                                            <button key={t} type="button" onClick={() => setForm({ ...form, event_type: t })}
                                                className={`flex flex-col items-center justify-center gap-1 p-2 rounded-md border text-[10px] font-semibold transition-colors ${form.event_type === t ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white dark:bg-[#1C222A] text-[#4B5563] dark:text-[#B5BAC1] border-[#E4E4E7] dark:border-[#252B33]"}`}>
                                                <cfg.Icon size={14} />
                                                <span>{cfg.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Început *</label>
                                    <input type="datetime-local" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required
                                        className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Sfârșit</label>
                                    <input type="datetime-local" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                                        className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm" />
                            </div>
                            {(form.event_type === "meeting_physical") && (
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Locație</label>
                                    <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                                        placeholder="Sală conferințe / adresă..."
                                        className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm" />
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Participanți</label>
                                <select multiple value={form.participants} onChange={e => {
                                    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                                    setForm({ ...form, participants: selected });
                                }} className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm min-h-[90px]">
                                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                                <p className="text-[10px] text-[#9CA3AF] mt-1">Ctrl/Cmd + click pentru selecție multiplă. Creatorul e adăugat automat.</p>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button type="submit" disabled={saving}
                                    data-testid="event-form-submit"
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se salvează..." : "Creează eveniment"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
